import { useEffect, useLayoutEffect, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header/Header';
import SectionNav from '@/components/SectionNav/SectionNav';
import Footer from '@/components/Footer/Footer';
import Section from '@/components/Section/Section';
import Article from '@/pages/Article/Article';
import Home from '@/pages/Home/Home';
import Work from '@/pages/Work/Work';
import About from '@/pages/About/About';
import Skills from '@/pages/Skills/Skills';
import Journey from '@/pages/Journey/Journey';
import Blog from '@/pages/Blog/Blog';
import Contact from '@/pages/Contact/Contact';
import MetaTags from '@/components/MetaTags/MetaTags';
import useActiveSectionId from '@/hooks/useActiveSectionId';
import { ROUTES, SECTION_VIDEOS, SectionId } from '@/shared/routes.ts';

const SECTION_COMPONENTS: Record<SectionId, ComponentType> = {
  home: Home,
  work: Work,
  about: About,
  skills: Skills,
  journey: Journey,
  blog: Blog,
  contact: Contact
};

type PageView = { type: 'home' } | { type: 'article'; slug: string };

const getPageView = (path: string): PageView => {
  const articleMatch = path.match(/^\/article\/([\w-]+)$/);
  if (articleMatch) return { type: 'article', slug: articleMatch[1] };

  return { type: 'home' };
};

const Router = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<PageView>(() => getPageView(window.location.pathname));
  const activeSectionId = useActiveSectionId(view.type === 'home');

  useEffect(() => {
    const sync = () => {
      const newView = getPageView(window.location.pathname);
      if (newView.type !== 'home') window.scrollTo(0, 0);
      else if (window.location.hash) sessionStorage.setItem('scrollTo', window.location.hash.slice(1));
      setView(newView);
    };

    window.addEventListener('popstate', sync);
    window.addEventListener('pushstate', sync);

    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener('pushstate', sync);
    };
  }, []);

  useEffect(() => {
    if (view.type !== 'home') return;
    history.replaceState(null, '', activeSectionId === 'home' ? location.pathname : `#${activeSectionId}`);
  }, [activeSectionId, view.type]);

  useLayoutEffect(() => {
    if (view.type !== 'home') return;
    const target = sessionStorage.getItem('scrollTo');
    sessionStorage.removeItem('scrollTo');
    if (!target) return;
    const el = document.getElementById(target);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY, behavior: 'instant' });
  }, [view]);

  if (view.type === 'article') return <Article slug={view.slug} />;

  return (
    <>
      <MetaTags
        title={t(`meta.sections.${activeSectionId}.title`)}
        description={t(`meta.sections.${activeSectionId}.description`)}
        url='https://dendo.dev'
        locale={t('meta.ogLocale')}
      />
      <SectionNav />
      <Header />
      <main>
        {ROUTES.map(({ id }) => {
          const Component = SECTION_COMPONENTS[id];
          return (
            <Section key={id} id={id} video={SECTION_VIDEOS[id]}>
              <Component />
            </Section>
          );
        })}
      </main>
      <Footer />
    </>
  );
};

export default Router;
